/**
 * `Alpine.store('pdp')` — single source of truth for the PDP section.
 *
 * State shape:
 *   - variantId        : currently-selected variant id (Number | null).
 *   - quantity         : selected quantity (Number).
 *   - handle           : product handle string, used to fetch payload.
 *   - productId        : numeric product id, broadcast with main-options.
 *   - moqMin/Step/Max  : resolved minimum order qty rule for the product.
 *   - oosIds           : Set<string> of variant ids out of stock per the
 *                        warehouse mirror metafield (server-rendered via
 *                        `variant-stock-state`). Replaces the payload's
 *                        `variant.available`, which is always true now
 *                        that every variant is natively CONTINUE.
 *   - channelHiddenIds : Set<string> of variant ids hidden from the Online
 *                        Store channel (server-rendered via
 *                        `variant-channel-hidden`). Filtered out of the
 *                        fetched payload in `loadPayload`, so no client
 *                        resolution can re-select them. Empty for an
 *                        all-online product — fail-open.
 *   - belowMoqIds      : Set<string> of variant ids whose warehouse stock
 *                        sits below their own MOQ (server-rendered).
 *   - variantMaxMap    : Map<string, number> of per-variant effective
 *                        upper bound (grid-snapped min of MOQ max and
 *                        warehouse stock). Missing entries mean
 *                        "unbounded" for that variant.
 *   - variantMoqMap    : Map<string, {min, step}> of each variant's MOQ
 *                        floor + increment. Missing entries mean 1/1. The
 *                        buy-buttons controller pushes these onto the
 *                        quantity selector on variant change.
 *   - hazardousBlockedIds
 *                      : Set<string> of hazardous variant ids locked
 *                        because the active market blocks hazardous
 *                        delivery (custom.block_hazardous_delivery).
 *                        The CTA locks when the active variant is a
 *                        member.
 *   - hazardousIds     : Set<string> of variant ids flagged hazardous.
 *   - bulkyIds         : Set<string> of variant ids flagged bulky. Both
 *                        drive the per-variant restriction notices.
 *   - ctaLabels        : translated CTA strings sourced from the
 *                        `[data-product-atc-cta]` wrapper's data attrs.
 *   - productPayload   : `/products/{handle}.js` payload once fetched.
 *   - moneyFormat      : Shopify money template (e.g. `${{amount}}`).
 *
 * Methods:
 *   - init(rootEl)              : idempotent — pulls config from data
 *                                 attrs on the section root + nested
 *                                 wrappers. Subsequent calls are no-ops.
 *   - loadPayload()             : fetches the product JSON and stores it.
 *   - setVariantId(id)          : updates `variantId` (Number).
 *   - findVariant(id)           : returns the variant entry from the
 *                                 payload, or null if not yet loaded.
 *   - resolveVariantFromValues  : matches an ordered values array
 *     (values)                   against the payload's variants.
 *   - formatMoney(cents)        : delegates to `window.theme.formatMoney`
 *                                 using the store's `moneyFormat`.
 *
 * Components subscribe via `$store.pdp` (inside Alpine context) or
 * `window.Alpine.store('pdp')` (from imperative bootstraps such as the
 * gallery component's pointer math). Cross-section consumers (colour
 * cards, find-in-store) keep listening to the document-level
 * `main-options:change` and `variant:change` events that the picker
 * dispatches.
 */
(function () {
  document.addEventListener('alpine:init', () => {
    window.Alpine.store('pdp', {
      ready: false,
      rootEl: null,
      handle: null,
      productId: null,
      variantId: null,
      quantity: 1,
      moqMin: 1,
      moqStep: 1,
      moqMax: Infinity,
      oosIds: new Set(),
      channelHiddenIds: new Set(),
      belowMoqIds: new Set(),
      variantMaxMap: new Map(),
      variantMoqMap: new Map(),
      hazardousBlockedIds: new Set(),
      hazardousIds: new Set(),
      bulkyIds: new Set(),
      ctaLabels: { default: '', soldOut: '', hazardousBlocked: '', selectTemplate: '' },
      productPayload: null,
      moneyFormat: (window.theme && window.theme.moneyFormat) || '',

      init(rootEl) {
        if (this.ready) return;
        if (!rootEl) return;
        this.ready = true;
        this.rootEl = rootEl;
        this.handle = rootEl.dataset.productHandle || null;
        this.productId = rootEl.dataset.productId || null;
        this.variantId = Number(rootEl.dataset.initialVariantId) || null;

        const quantityRoot = rootEl.querySelector('[data-product-quantity]');
        if (quantityRoot) {
          this.moqMin = Number(quantityRoot.dataset.moqMin) || 1;
          this.moqStep = Number(quantityRoot.dataset.moqStep) || 1;
          this.moqMax =
            quantityRoot.dataset.moqMax && quantityRoot.dataset.moqMax !== ''
              ? Number(quantityRoot.dataset.moqMax)
              : Infinity;
          this.quantity = this.moqMin;
          const parseIdSet = (raw) =>
            new Set(
              (raw || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            );
          this.oosIds = parseIdSet(quantityRoot.dataset.oosIds);
          /* Variants hidden from the Online Store channel (B2B catalog
             context). Excluded from the fetched payload in `loadPayload`
             so the picker can neither resolve nor re-select them. Empty
             empty for an all-online product — fail-open. */
          this.channelHiddenIds = parseIdSet(quantityRoot.dataset.channelHiddenIds);
          this.belowMoqIds = parseIdSet(quantityRoot.dataset.belowMoqIds);
          this.variantMaxMap = new Map(
            (quantityRoot.dataset.variantMaxMap || '')
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean)
              .map((entry) => {
                const [id, cap] = entry.split(':');
                return [id, Number(cap)];
              })
              .filter(([id, cap]) => id && Number.isFinite(cap))
          );
          this.variantMoqMap = new Map(
            (quantityRoot.dataset.variantMoqMap || '')
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean)
              .map((entry) => {
                const [id, min, step] = entry.split(':');
                return [id, { min: Number(min) || 1, step: Number(step) || 1 }];
              })
              .filter(([id]) => id)
          );
          this.hazardousBlockedIds = parseIdSet(quantityRoot.dataset.hazardousBlockedIds);
          this.hazardousIds = parseIdSet(quantityRoot.dataset.hazardousIds);
          this.bulkyIds = parseIdSet(quantityRoot.dataset.bulkyIds);
        }

        const ctaWrapper = rootEl.querySelector('[data-product-atc-cta]');
        if (ctaWrapper) {
          this.ctaLabels = {
            default: ctaWrapper.dataset.ctaLabelDefault || '',
            soldOut: ctaWrapper.dataset.ctaLabelSoldOut || '',
            hazardousBlocked: ctaWrapper.dataset.ctaLabelHazardousBlocked || '',
            selectTemplate: ctaWrapper.dataset.ctaLabelSelectTemplate || '',
          };
        }
      },

      loadPayload() {
        if (this.productPayload) return Promise.resolve(this.productPayload);
        if (!this.handle) return Promise.resolve(null);
        if (!window.theme || typeof window.theme.loadProductJson !== 'function') {
          return Promise.resolve(null);
        }
        return window.theme.loadProductJson(this.handle, this.channelHiddenIds).then((payload) => {
          this.productPayload = payload;
          return payload;
        });
      },

      setVariantId(id) {
        this.variantId = id == null ? null : Number(id);
      },

      findVariant(id) {
        if (!this.productPayload || id == null) return null;
        const target = String(id);
        return this.productPayload.variants.find((v) => String(v.id) === target) || null;
      },

      /* Both variant-lookup helpers delegate to the shared
         `window.theme.variantUtils` module so the PDP and the Quick View
         drawer stay in lockstep. The store keeps the tiny wrapper
         methods so existing callers (`$store.pdp.resolveVariantFromValues(...)`)
         don't have to change import sites. */
      resolveVariantFromValues(values) {
        var utils = window.theme && window.theme.variantUtils;
        if (!utils) return null;
        return utils.resolveVariantFromValues(this.productPayload, values);
      },

      hasVariantForPartial(selection) {
        var utils = window.theme && window.theme.variantUtils;
        if (!utils) return true;
        return utils.hasVariantForPartial(this.productPayload, selection);
      },

      formatMoney(cents) {
        if (window.theme && typeof window.theme.formatMoney === 'function') {
          return window.theme.formatMoney(cents, this.moneyFormat);
        }
        return `$${(cents / 100).toFixed(2)}`;
      },
    });
  });
})();
